import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';
// selectors
import { useConfigSelectors } from 'src/app/reducers/configReducer';
import { useLanguageSelectors } from 'src/app/reducers/languageReducer';
import { useDataSelectors } from 'src/@infringementsCandidates/reducers';
import { InfringementDispatchContext } from 'src/@infringementsCandidates/providers/InfringementsStateProvider';
// actions
import { selectRow } from 'src/@infringementsCandidates/actions/infringementsActions';
import { dataActionTypes } from 'src/@infringementsCandidates/newActionTypes';
// components
import { Table } from '@ems/client-design-system';
// function
import { capitalizeObjectKeys } from 'src/utils/objectModifiers';
import {
  formatInfringementHeaders,
  formatInfringementData,
} from 'src/@infringementsCandidates/functions';

import { UPDATE_INFRINGEMENT_STATUS } from 'src/@infringementsCandidates/mutations';
import { IInfringementsData } from 'src/@infringementsCandidates/interfaces';
import { hideInfringementColumns } from 'src/utils/tableColumns';
import { InfringementType, useGetInfringements } from 'src/@infringements/hooks';
import { LoadMoreBar } from 'src/componentsModern';
import { useInfringementDataTable } from 'src/@infringements/reducers';
import {
  GetInfringementCandidatesQuery,
  GetInfringementsQuery,
} from 'src/app/gql/__generated__/hooks';
import { InfringementTableDataActionType } from 'src/@infringements/reducers/tableData';
import { useScreenLoadStateContext } from 'src/app/providers/ScreenLoadStateContext';

export const TableContainer = ({
  candidateFlag,
  setCandidateFlag,
}: {
  candidateFlag: boolean;
  setCandidateFlag: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const newDispatcher = useContext<any>(InfringementDispatchContext);
  const [updateInfringementStatus] = useMutation(UPDATE_INFRINGEMENT_STATUS, {
    update(cache, { data: { updateInfringement } }) {
      const id = updateInfringement.id;
      const status = updateInfringement.status;

      newDispatcher({
        type: dataActionTypes.INLINE_EDIT,
        data: {
          id,
          status,
        },
      });
    },
  });

  const {
    data,
    loading: tableLoading,
    fetchMore,
    refetch,
    loadingMore: tableLoadingMore,
  } = useGetInfringements(newDispatcher, InfringementType.CANDIDATE);
  const { state, dispatch } = useInfringementDataTable();

  const handleGetMore = (): void => {
    if (!data || !data?.pageInfo?.hasNextPage) {
      console.log('No more data to load');
      return;
    }

    void fetchMore({
      variables: {
        after: data.pageInfo.endCursor,
        first: loadMoreSize,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        if (state.hasCandidatesEnabled) {
          const typedPrev = prev as GetInfringementCandidatesQuery;
          const typedFetchMoreResult = fetchMoreResult as GetInfringementCandidatesQuery;

          return {
            ...typedPrev,
            infringementCandidateSummariesByTimeRange: {
              ...typedPrev.infringementCandidateSummariesByTimeRange,
              items: [
                ...(typedPrev.infringementCandidateSummariesByTimeRange?.items || []),
                ...(typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.items || []),
              ],
              pageInfo:
                typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.pageInfo ||
                typedPrev.infringementCandidateSummariesByTimeRange?.pageInfo,
              totalCount:
                typedFetchMoreResult.infringementCandidateSummariesByTimeRange?.totalCount ||
                typedPrev.infringementCandidateSummariesByTimeRange?.totalCount,
            },
          };
        } else {
          const typedPrev = prev as GetInfringementsQuery;
          const typedFetchMoreResult = fetchMoreResult as GetInfringementsQuery;

          return {
            ...typedPrev,
            infringementSummariesByTimeRange: {
              ...typedPrev.infringementSummariesByTimeRange,
              items: [
                ...(typedPrev.infringementSummariesByTimeRange?.items || []),
                ...(typedFetchMoreResult.infringementSummariesByTimeRange?.items || []),
              ],
              pageInfo:
                typedFetchMoreResult.infringementSummariesByTimeRange?.pageInfo ||
                typedPrev.infringementSummariesByTimeRange?.pageInfo,
              totalCount:
                typedFetchMoreResult.infringementSummariesByTimeRange?.totalCount ||
                typedPrev.infringementSummariesByTimeRange?.totalCount,
            },
          };
        }
      },
    });
  };

  // Configuration
  const configSelectors = useConfigSelectors();
  const {
    globals: { '12HourFormat': twelveHourFormat },
    grid: { resultSize, loadMoreSize },
    infringements: {
      grid: { hiddenColumns },
    },
  } = configSelectors.getConfig();
  // Translation
  const languageSelectors = useLanguageSelectors();
  const translationStrings = languageSelectors.getLanguage();
  const {
    components: {
      buttons: { loadMore: loadMoreText },
      tooltip: { candidates: candidatesTooltipText },
      labels: {
        table: { endTable },
      },
      hints: { tryChangingFiltersOrDate },
      lists: {
        aircraftCategories,
        candidates,
        operationTypes,
        ruleTypes,
        severities,
        infringementStatuses,
      },
    },
    screens: {
      infringements: {
        errors: { noDataFound },
      },
    },
  } = translationStrings;
  const dataSelectors = useDataSelectors();
  const {
    data: latestData,
    pageInfo,
    selectedData,
    areAllRowsSelected,
    isLoadingMore,
  } = dataSelectors.getDataInformation();

  const [translationDataList, setTranslationDataList] = useState<object[]>([]);
  useEffect(() => {
    setTranslationDataList(
      capitalizeObjectKeys({
        ...aircraftCategories,
        ...candidates,
        ...operationTypes,
        ...ruleTypes,
        ...severities,
        ...infringementStatuses,
      })
    );
  }, [aircraftCategories, candidates, operationTypes, ruleTypes, severities, infringementStatuses]);
  // Connect map load state to table load state
  const { screenLoadStates, setScreenLoadStates } = useScreenLoadStateContext();
  const { mapLoadState, tableLoadState } = screenLoadStates;
  const loading = tableLoading || isLoadingMore;
  useEffect(() => {
    setScreenLoadStates({ type: 'SET_TABLE_LOAD_STATE', payload: tableLoading });
  }, [tableLoading]);

  // Always 'show' all columns, then hide the ones we don't need.
  const columns = [
    'displayRuleName',
    'displayTime',
    'displayInfringement',
    'displaySeverity',
    'acid',
    'displayCategory',
    'aircraftType',
    'displayStatus',
    'displayCandidates',
  ];

  const hiddenColumnsNonNull = hiddenColumns ? hiddenColumns : [];
  const showSeverity = !hiddenColumnsNonNull.includes('displaySeverity');
  const showCandidates = !hiddenColumnsNonNull.includes('displayCandidates');
  const showStatus = !hiddenColumnsNonNull.includes('displayStatus');
  // column types are used to add class-names to each column (used only for styling)
  // TODO: this logic needs to be handled in the design system, not here. We'll refactor when the table needs to be modified.
  const columnTypes = {
    rule: {
      title: 'rule',
      abbreviation: '',
    },
    acid: {
      title: 'record-title',
      abbreviation: '',
    },
    displayRunwayName: {
      title: 'runway',
      abbreviation: 'Rwy',
    },
    displayCategory: {
      title: 'aircraft',
      abbreviation: 'Categ',
    },
    displayTime: {
      title: 'time',
      abbreviation: '',
    },
    displayStatus: {
      title: 'status',
      abbreviation: '',
    },
    displayInfringement: {
      title: 'infringement',
      abbreviation: '',
      grow: showSeverity ? null : '2',
    },
    displaySeverity: {
      title: 'severity',
      abbreviation: '',
      grow: '2',
    },
    aircraftType: {
      title: 'model',
      abbreviation: '',
      grow: '3',
    },
    displayCandidates: {
      title: 'candidates',
      abbreviation: '',
    },
  };

  const handleInlineDropdown = useCallback((infringementId, newStatus, infringementType) => {
    updateInfringementStatus({
      variables: { id: infringementId, status: newStatus },
      optimisticResponse: {
        __typename: infringementType,
        updateInfringement: {
          id: infringementId,
          status: newStatus,
          __typename: infringementType,
        },
      },
    }).then(() => {
      refetch();
    });
  }, []);

  const onSelectRow = id => {
    selectRow(id, newDispatcher);
  };

  hideInfringementColumns({
    showSeverity,
    showStatus,
    showCandidates,
  });

  const [displayData, setDisplayData] = useState<IInfringementsData[]>([]);
  const { itemsIds, hasNextPage, endCursor, dateRange } = dataSelectors.getNavigationData();
  const ruleInfo = { itemsIds, hasNextPage, endCursor, dateRange };
  useEffect(() => {
    setDisplayData(
      formatInfringementData(
        tableLoading && !tableLoadingMore ? [] : data?.items || [],
        translationDataList,
        state.hasCandidatesEnabled,
        ruleInfo,
        twelveHourFormat
      )
    );
  }, [data, latestData, translationDataList, handleInlineDropdown, tableLoading, tableLoadingMore]);

  const handleCandidatesChange = bool => {
    setCandidateFlag(bool);
    newDispatcher({ type: dataActionTypes.SET_CANDIDATES_ENABLED, data: bool });
    //resetAndFetchData(client, resultSize, newDispatcher, sortString, bool); // fetch the next page
    dispatch({
      type: InfringementTableDataActionType.TOGGLE_CANDIDATES,
      payload: bool,
    });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Handle showCandidates parameter
    const showCandidates = urlParams.get('showCandidates');
    if (showCandidates === 'true' && state.variables.filter.ruleNames) {
      handleCandidatesChange(true);
    }

    // Handle isInfringement parameter
    const isInfringement = urlParams.get('isInfringement');
    if (isInfringement && showCandidates === 'true') {
      console.log('Setting isInfringement filter to:', isInfringement);
      dispatch({
        type: InfringementTableDataActionType.UPDATE_BOOLEAN_FILTER,
        payload: {
          filter: 'isInfringement',
          value: isInfringement === 'true',
        },
      });
    }
  }, []);

  useEffect(() => {
    // Update URL when candidates or infringement filter changes
    const updateURL = () => {
      const url = new URL(window.location.href);

      // Handle showCandidates parameter
      if (state.hasCandidatesEnabled) {
        url.searchParams.set('showCandidates', 'true');
      } else {
        url.searchParams.delete('showCandidates');
      }

      // Handle isInfringement parameter
      if (typeof state.variables.filter.isInfringement === 'boolean') {
        url.searchParams.set('isInfringement', state.variables.filter.isInfringement.toString());
      } else {
        url.searchParams.delete('isInfringement');
      }

      window.history.replaceState({}, '', url.toString());
    };

    updateURL();
  }, [state.hasCandidatesEnabled, state.variables.filter.isInfringement]);

  return (
    <>
      <Table
        className={`infringements-table infringements-table--regular`}
        loading={(tableLoadState && mapLoadState) || tableLoadingMore}
        rowHeaders={formatInfringementHeaders(
          resultSize,
          loading,
          state.hasCandidatesEnabled,
          handleCandidatesChange,
          !!state.variables.filter.ruleNames,
          candidatesTooltipText,
          translationStrings
        )}
        data={displayData}
        columns={columns}
        columnTypes={columnTypes}
        selectedData={selectedData}
        areAllRowsSelected={areAllRowsSelected}
        gridID="infringementsCandidates"
        onSelectRow={onSelectRow}
        hasEnded={latestData.length && pageInfo && !pageInfo.hasNextPage}
        languageData={{ noDataTitle: noDataFound, noDataText: tryChangingFiltersOrDate, endTable }}
      />
      {!!data?.pageInfo?.hasNextPage && (
        <LoadMoreBar isLoading={tableLoadingMore} onClick={handleGetMore} disabled={tableLoading}>
          {loadMoreText}
        </LoadMoreBar>
      )}
    </>
  );
};
